"use client";

import * as React from "react";
import { toast } from "sonner";
import {
  Copy,
  ReceiptText,
  Search,
} from "lucide-react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  faDateTime,
  faToman,
  formatCard,
} from "@/lib/format";
import type { PaymentWithRelations, PaymentStatus } from "./types";
import ApproveDialog from "./ApproveDialog";

type Props = { onRefresh: () => void };
type Filter = "ALL" | PaymentStatus;

export default function AllPayments({ onRefresh }: Props) {
  const [payments, setPayments] = React.useState<PaymentWithRelations[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [filter, setFilter] = React.useState<Filter>("ALL");
  const [query, setQuery] = React.useState("");
  const [detail, setDetail] = React.useState<PaymentWithRelations | null>(
    null,
  );
  const [approveOpen, setApproveOpen] = React.useState(false);
  const [approvePayment, setApprovePayment] =
    React.useState<PaymentWithRelations | null>(null);

  const loadPayments = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/admin/payments", { cache: "no-store" });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || "خطا در دریافت پرداخت‌ها");
      }
      setPayments(data.payments ?? []);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "خطا در دریافت پرداخت‌ها";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  const filtered = React.useMemo(() => {
    return payments.filter((p) => {
      if (filter !== "ALL" && p.status !== filter) return false;
      if (query.trim()) {
        const q = query.trim();
        const inText =
          p.user?.name?.includes(q) ||
          p.user?.phone?.includes(q) ||
          p.trackingCode?.includes(q) ||
          p.senderCard?.includes(q);
        if (!inText) return false;
      }
      return true;
    });
  }, [payments, filter, query]);

  function copyVless(link: string) {
    navigator.clipboard
      .writeText(link)
      .then(() => toast.success("لینک کپی شد."))
      .catch(() => toast.error("کپی لینک ناموفق بود."));
  }

  function openApprove(p: PaymentWithRelations) {
    setDetail(null);
    setApprovePayment(p);
    setApproveOpen(true);
  }

  function handleApproveDone() {
    loadPayments();
    onRefresh();
  }

  return (
    <div className="flex flex-col gap-4">
      <Card className="rounded-2xl border-border/60 shadow-sm">
        <CardHeader className="border-b">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="text-base">همه پرداخت‌ها</CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  dir="ltr"
                  placeholder="جستجو بر اساس نام / تلفن / کارت"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="h-9 w-56 pr-9 text-left text-sm"
                />
              </div>
              <Select
                value={filter}
                onValueChange={(v) => setFilter(v as Filter)}
              >
                <SelectTrigger className="h-9 w-36 text-sm">
                  <SelectValue placeholder="همه" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">همه</SelectItem>
                  <SelectItem value="PENDING">در انتظار</SelectItem>
                  <SelectItem value="APPROVED">تأیید شده</SelectItem>
                  <SelectItem value="REJECTED">رد شده</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full rounded-md" />
              ))}
            </div>
          ) : error ? (
            <div className="py-8 text-center text-sm text-destructive">
              {error}
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              پرداختی یافت نشد.
            </div>
          ) : (
            <div className="max-h-[28rem] overflow-y-auto scrollbar-thin">
              <Table>
                <TableHeader className="sticky top-0 bg-card">
                  <TableRow>
                    <TableHead className="pr-4">کاربر</TableHead>
                    <TableHead>طرح</TableHead>
                    <TableHead>مبلغ</TableHead>
                    <TableHead>کارت فرستنده</TableHead>
                    <TableHead>وضعیت</TableHead>
                    <TableHead>تاریخ</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((p) => (
                    <TableRow
                      key={p.id}
                      className="cursor-pointer"
                      onClick={() => setDetail(p)}
                    >
                      <TableCell className="pr-4">
                        <div className="flex flex-col">
                          <span className="font-medium">{p.user.name}</span>
                          <span
                            dir="ltr"
                            className="text-xs text-muted-foreground"
                          >
                            {p.user.phone}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{p.plan.name}</TableCell>
                      <TableCell className="text-sm font-semibold text-primary">
                        {faToman(p.amountToman)}
                      </TableCell>
                      <TableCell
                        dir="ltr"
                        className="font-mono text-xs text-muted-foreground"
                      >
                        {p.senderCard ? formatCard(p.senderCard) : "—"}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={p.status} />
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                        {faDateTime(p.createdAt)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail dialog */}
      <Dialog
        open={!!detail}
        onOpenChange={(o) => !o && setDetail(null)}
      >
        <DialogContent className="max-w-lg rounded-2xl">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <ReceiptText className="size-5 text-primary" />
                  جزئیات پرداخت
                </DialogTitle>
                <DialogDescription>
                  اطلاعات کامل این پرداخت و اشتراک مرتبط.
                </DialogDescription>
              </DialogHeader>

              <div className="grid grid-cols-1 gap-3 rounded-xl border bg-muted/40 p-4 sm:grid-cols-2">
                <DetailField
                  label="کاربر"
                  value={`${detail.user.name} — ${detail.user.phone}`}
                  dir="ltr"
                />
                <DetailField label="طرح" value={detail.plan.name} />
                <DetailField
                  label="مبلغ"
                  value={faToman(detail.amountToman)}
                  primary
                />
                <DetailField
                  label="کارت فرستنده"
                  value={detail.senderCard ? formatCard(detail.senderCard) : "—"}
                  dir="ltr"
                  mono
                />
                {detail.trackingCode && (
                  <DetailField
                    label="کد پیگیری"
                    value={detail.trackingCode}
                    dir="ltr"
                    mono
                  />
                )}
                <DetailField
                  label="تاریخ"
                  value={faDateTime(detail.createdAt)}
                />
                {detail.reviewedAt && (
                  <DetailField
                    label="بررسی شده در"
                    value={faDateTime(detail.reviewedAt)}
                  />
                )}
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">وضعیت:</span>
                <StatusBadge status={detail.status} />
              </div>

              {detail.note && (
                <div className="rounded-lg border bg-muted/40 p-3 text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">یادداشت کاربر: </span>
                  {detail.note}
                </div>
              )}

              {detail.adminNote && (
                <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-xs">
                  <span className="font-medium text-primary">یادداشت ادمین: </span>
                  <span className="text-muted-foreground">{detail.adminNote}</span>
                </div>
              )}

              {detail.receiptImage && (
                <a
                  href={detail.receiptImage}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block overflow-hidden rounded-xl border bg-muted/40"
                >
                  <img
                    src={detail.receiptImage}
                    alt="تصویر رسید"
                    className="max-h-60 w-full object-contain"
                  />
                </a>
              )}

              {detail.status === "APPROVED" &&
                detail.subscription?.vlessLink && (
                  <div className="rounded-xl border border-primary/30 bg-primary/5 p-3">
                    <div className="mb-1 flex items-center justify-between">
                      <span className="text-xs font-medium text-primary">
                        لینک اشتراک (VLESS)
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          copyVless(detail.subscription!.vlessLink!)
                        }
                      >
                        <Copy className="size-3.5" />
                        کپی
                      </Button>
                    </div>
                    <Input
                      dir="ltr"
                      readOnly
                      value={detail.subscription.vlessLink}
                      className="text-left font-mono text-xs"
                    />
                    {detail.subscription.endDate && (
                      <div className="mt-2 text-xs text-muted-foreground">
                        اعتبار تا: {faDateTime(detail.subscription.endDate)}
                      </div>
                    )}
                  </div>
                )}

              {detail.status === "PENDING" && (
                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <Button
                    className="bg-primary hover:bg-primary/90"
                    onClick={() => openApprove(detail)}
                  >
                    تأیید و فعال‌سازی
                  </Button>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <ApproveDialog
        payment={approvePayment}
        open={approveOpen}
        onClose={() => setApproveOpen(false)}
        onDone={handleApproveDone}
      />
    </div>
  );
}

function StatusBadge({ status }: { status: PaymentStatus }) {
  if (status === "APPROVED")
    return (
      <Badge className="bg-primary/15 text-primary hover:bg-primary/20">
        تأیید شده
      </Badge>
    );
  if (status === "REJECTED")
    return <Badge variant="destructive">رد شده</Badge>;
  return (
    <Badge className="bg-amber-500/15 text-amber-600 hover:bg-amber-500/20 dark:text-amber-400">
      در انتظار
    </Badge>
  );
}

function DetailField({
  label,
  value,
  primary,
  mono,
  dir,
}: {
  label: string;
  value: string;
  primary?: boolean;
  mono?: boolean;
  dir?: "ltr" | "rtl";
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span
        dir={dir}
        className={`text-sm ${mono ? "font-mono text-left" : ""} ${
          primary ? "font-semibold text-primary" : "font-medium"
        }`}
      >
        {value}
      </span>
    </div>
  );
}
